import type { Format } from './SaloonLobby';

/**
 * A one-shot "open the lobby on this tab" request, for a caller that sends the
 * player to the lobby while it is not mounted (EN-11109: leaving a finished
 * Sit & Go lands on the Sit & Go tab). showLobbyFormat's event would be lost:
 * the lobby is unmounted while a table is on screen and always mounts on Cash.
 *
 * SaloonLobbyContainer peeks it on mount and hands it to SaloonLobby's
 * forceFormat, the same channel the post-login tournament resume uses. It
 * expires so a landing armed for a navigation that never reached the lobby
 * cannot hijack a later, unrelated visit.
 *
 * The lobby page holds it while it loads: Lobby renders nothing until the
 * socket and the lobby feed are ready, so on a slow reconnect the container
 * mounts after the TTL. Once the page has gone the TTL runs again, from then.
 */
export const LOBBY_LANDING_TTL_MS = 10_000;

type PendingLanding = { format: Format; armedAtMs: number };

let pending: PendingLanding | null = null;
let heldByLobbyPage = false;

export const armLobbyLanding = (format: Format, now: number = Date.now()): void => {
  pending = { format, armedAtMs: now };
};

export const peekLobbyLanding = (now: number = Date.now()): Format | null => {
  if (!pending) return null;
  if (!heldByLobbyPage && now - pending.armedAtMs > LOBBY_LANDING_TTL_MS) {
    pending = null;
    return null;
  }
  return pending.format;
};

export const clearLobbyLanding = (): void => {
  pending = null;
  heldByLobbyPage = false;
};

/** The lobby page has mounted: a landing still live waits for its container. */
export const holdLobbyLanding = (now: number = Date.now()): void => {
  if (peekLobbyLanding(now)) heldByLobbyPage = true;
};

/** The lobby page has gone: a landing it did not hand over expires as usual. */
export const releaseLobbyLanding = (now: number = Date.now()): void => {
  if (!heldByLobbyPage) return;
  heldByLobbyPage = false;
  if (pending) pending = { ...pending, armedAtMs: now };
};
